/** Labels and tones for lead / tour / message states (SPEC §5.1, §3.4, §3.8). */
import type { LeadStatus } from "@shared/schemas/leads";
import type { ChipTone } from "@web/theme/theme";
import { fmtDay } from "./format";

import { LEAD_STATUS_LABEL } from "@shared/leadCard";
export { LEAD_STATUS_LABEL };
export const LEAD_STATUS_TONE: Record<LeadStatus, ChipTone> = {
  new: "accent",
  contacted: "neutral",
  tour_scheduled: "warn",
  toured: "warn",
  nurture: "neutral",
  converted: "success",
  lost: "danger",
  no_contact: "neutral",
  dnc: "danger",
};
export const leadStatusLabel = (s: string) => LEAD_STATUS_LABEL[s as LeadStatus] ?? s;
export const leadStatusTone = (s: string): ChipTone => LEAD_STATUS_TONE[s as LeadStatus] ?? "neutral";
/** Order of the status chips on the Leads screen. */
export const LEAD_STATUS_ORDER: LeadStatus[] = ["new", "contacted", "tour_scheduled", "toured", "nurture", "converted", "lost", "no_contact", "dnc"];
/** Hand-pickable statuses (SPEC §5.1); `tour_scheduled` / `converted` come from actions. */
export const PICKABLE_LEAD_STATUSES: LeadStatus[] = ["new", "contacted", "toured", "nurture", "no_contact", "lost", "dnc"];

export const TOUR_STATUS_LABEL: Record<string, string> = { scheduled: "Scheduled", completed: "Completed", no_show: "No-show", cancelled: "Cancelled" };
export const TOUR_STATUS_TONE: Record<string, ChipTone> = { scheduled: "accent", completed: "success", no_show: "warn", cancelled: "neutral" };
export const DISPOSITION_LABEL: Record<string, string> = { hot: "Hot", warm: "Warm", cold: "Cold", not_a_fit: "Not a fit" };
export const tourStatusLabel = (s: string) => TOUR_STATUS_LABEL[s] ?? s;
export const tourStatusTone = (s: string): ChipTone => TOUR_STATUS_TONE[s] ?? "neutral";


/** Message status → short caption ("Delivered", "Failed", …). */
export const MESSAGE_STATUS_LABEL: Record<string, string> = {
  held: "Held",
  queued: "Queued",
  sending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  failed: "Failed",
  bounced: "Bounced",
  cancelled: "Cancelled",
  received: "Received",
  logged: "Logged",
};
export const messageStatusLabel = (s: string) => MESSAGE_STATUS_LABEL[s] ?? s;
export const isFailedStatus = (s: string) => s === "failed" || s === "bounced";

/** ISO timestamp → "just now" · "5m" · "3h" · "2d" · "Sep 3" (compact, for list rows). */
export function fmtAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "";
  const diff = now - new Date(iso).getTime();
  const m = Math.round(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d`;
  return fmtDay(iso.slice(0, 10));
}

/** Send / schedule warnings from the Worker → a sentence for a toast; unknown keys → null (hide). */
export function warningMessage(w: string | null | undefined): string | null {
  if (!w) return null;
  if (w === "quiet_hours") return "Outside 8am–9pm. Automated confirmations wait until sending hours; manual sends are immediate.";
  if (w === "no phone: confirmation not sent") return "No phone on file — confirmation not sent";
  if (w === "no SMS consent: confirmation not sent") return "No SMS consent — confirmation not sent";
  if (w.startsWith("confirmation failed:")) return `Confirmation text failed — ${w.slice("confirmation failed:".length).trim()}`;
  if (w.startsWith("confirmation not sent:")) return `Confirmation not sent — ${w.slice("confirmation not sent:".length).trim()}`;
  return null;
}

/** "just now" · "5m ago" · "Sep 3" — `fmtAgo` with the suffix where it reads right. */
export function fmtSince(iso: string | null | undefined, now = Date.now()): string {
  const s = fmtAgo(iso, now);
  return s === "just now" || !/^\d+[mhd]$/.test(s) ? s : `${s} ago`;
}

/** ISO timestamp → "2:05 PM" today, else "Sep 3, 2:05 PM" (thread captions). */
export function fmtClock(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return new Intl.DateTimeFormat("en-US", { ...(sameDay ? {} : { month: "short", day: "numeric" }), hour: "numeric", minute: "2-digit" }).format(d);
}

/** Local `YYYY-MM-DDTHH:MM` for a `datetime-local` input from an ISO timestamp (browser zone). */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export const localInputToIso = (local: string) => {
  if (!local) return null;
  const value = new Date(local);
  // Keep malformed draft evidence for the shared schema instead of crashing a
  // render/save handler before it can display the field finding.
  return Number.isNaN(value.getTime()) ? local : value.toISOString();
};

/** Row title for a lead: contact name → email → phone → "No name". */
export const leadTitle = (l: { contactName?: string | null; contactEmail?: string | null; contactPhone?: string | null }) => (l.contactName ?? "").trim() || l.contactEmail || l.contactPhone || "No name";
